import type { ComponentChildren } from 'preact';
import { NeedDot, WorthRing, chipCount } from './bits';
import { EdgeScroll } from './EdgeScroll';

// The one view switch (consistency pass R3 P3, Kristi Nov 2): the options sit together on one
// shaded grey track and exactly one is chosen, drawn raised with 600 weight. It chooses which
// list or layout you see (By book | By date, Covers | List, Review's lists). An option may carry
// a count in [brackets] and the needs-you dot. With more than five options, a thin rule and a
// small label split them into groups; `stacked` puts each group on its own line under its label
// (Update details: To review, then Lookups; Kristi, Nov 3: easier to read). A long switch on a phone
// scrolls with the edge (`edge`, R9 M2). Filter chips narrow a list instead: they're `Chip`.

export interface SegOption<K extends string> {
  key: K;
  name: ComponentChildren;
  /** A count, shown as [n] (R7 N3). */
  count?: number;
  /** Something here needs you: the dot, with this tip ("Needs you: 23 new books"). */
  need?: string;
  /** Something here is worth a look: the ring, with this tip (filters review F2). */
  worth?: string;
  title?: string;
}

export interface SegGroup<K extends string> {
  /** The small label before the group ("To review", "Lookups"). */
  label: string;
  options: SegOption<K>[];
}

type OptionInput<K extends string> = SegOption<K> | [K, ComponentChildren];

const toOption = <K extends string>(o: OptionInput<K>): SegOption<K> => (Array.isArray(o) ? { key: o[0], name: o[1] } : o);

export function Segmented<K extends string>({
  label,
  options,
  groups,
  value,
  onChange,
  edge,
  stacked,
  class: cls = '',
}: {
  /** What the switch chooses, for screen readers ("Show history", "Group"). */
  label: string;
  /** The options, as {key, name, count?, need?} or [key, name]. */
  options?: OptionInput<K>[];
  /** Or the options in labelled groups, split by a thin rule. */
  groups?: SegGroup<K>[];
  value: K;
  onChange: (k: K) => void;
  /** On a phone the switch stays on one line and scrolls with the edge fade and arrows. */
  edge?: boolean;
  /** Groups on their own lines, each under its small label: still one switch. */
  stacked?: boolean;
  class?: string;
}) {
  const button = (o: SegOption<K>) => (
    <button
      key={o.key}
      type="button"
      role="radio"
      aria-checked={o.key === value}
      class={o.key === value ? 'on' : ''}
      title={o.title}
      onClick={() => onChange(o.key)}
    >
      {o.need ? <NeedDot tip={o.need} /> : o.worth ? <WorthRing tip={o.worth} /> : null}
      {o.name}
      {o.count !== undefined ? <span class="seg-count">{chipCount(o.count)}</span> : null}
    </button>
  );
  if (groups && stacked) {
    const live = groups.filter((g) => g.options.length);
    return (
      <div class={`seg-stack${cls ? ` ${cls}` : ''}`} role="radiogroup" aria-label={label}>
        {live.map((g) => {
          const row = (
            <span class="seg" role="group" aria-label={g.label}>
              {g.options.map(button)}
            </span>
          );
          return (
            <div key={g.label} class="seg-stack-row">
              <span class="seg-stack-label" aria-hidden="true">
                {g.label}
              </span>
              {edge ? (
                <EdgeScroll label={g.label} chosen={g.options.some((o) => o.key === value) ? value : undefined}>
                  {row}
                </EdgeScroll>
              ) : (
                row
              )}
            </div>
          );
        })}
      </div>
    );
  }
  const track = (
    <span class={`seg${groups ? ' grouped' : ''}${cls ? ` ${cls}` : ''}`} role="radiogroup" aria-label={label}>
      {groups
        ? groups
            .filter((g) => g.options.length)
            .map((g, i) => (
              <span key={g.label} class="seg-group" role="group" aria-label={g.label}>
                {i ? <span class="seg-rule" aria-hidden="true" /> : null}
                <span class="seg-label" aria-hidden="true">
                  {g.label}
                </span>
                {g.options.map(button)}
              </span>
            ))
        : (options ?? []).map(toOption).map(button)}
    </span>
  );
  return edge ? (
    <EdgeScroll label={label} chosen={value}>
      {track}
    </EdgeScroll>
  ) : (
    track
  );
}
